import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Box,
  CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LockResetIcon from '@mui/icons-material/LockReset';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { User } from '../../types';

interface UserTableProps {
  users: User[];
  isLoading: boolean;
  togglingAdminUsername: string | null;
  togglingEnabledUsername: string | null;
  currentUserIsRoot: boolean;
  onDelete: (username: string) => void;
  onSetPassword: (username: string) => void;
  onToggleAdmin: (username: string) => void;
  onToggleEnabled: (username: string) => void;
}

export function UserTable({
  users,
  isLoading,
  togglingAdminUsername,
  togglingEnabledUsername,
  currentUserIsRoot,
  onDelete,
  onSetPassword,
  onToggleAdmin,
  onToggleEnabled,
}: UserTableProps): JSX.Element {
  const canModifyUser = (user: User): boolean => {
    if (user.isRoot && !currentUserIsRoot) {
      return false;
    }
    return true;
  };

  if (isLoading && users.length === 0) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Username</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Enabled</TableCell>
            <TableCell>Role</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.username} hover>
              <TableCell>{user.username}</TableCell>
              <TableCell>{user.email || '-'}</TableCell>
              <TableCell>
                <Chip
                  label={user.enabled ? 'Yes' : 'No'}
                  color={user.enabled ? 'success' : 'error'}
                  size="small"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                {togglingAdminUsername === user.username ? (
                  <CircularProgress size={20} />
                ) : (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {user.isRoot && (
                      <Chip label="ROOT" color="error" size="small" variant="filled" />
                    )}
                    {user.isAdmin && (
                      <Chip label="Admin" color="primary" size="small" variant="filled" />
                    )}
                    {!user.isRoot && !user.isAdmin && (
                      <Chip label="User" color="default" size="small" variant="outlined" />
                    )}
                  </Box>
                )}
              </TableCell>
              <TableCell align="right">
                {!user.isRoot && (
                  <Tooltip
                    title={
                      !canModifyUser(user)
                        ? 'Cannot modify root user'
                        : user.isAdmin
                          ? 'Remove Admin'
                          : 'Make Admin'
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        color={user.isAdmin ? 'warning' : 'primary'}
                        onClick={() => onToggleAdmin(user.username)}
                        aria-label={user.isAdmin ? 'remove admin' : 'make admin'}
                        disabled={!canModifyUser(user) || togglingAdminUsername === user.username}
                      >
                        {togglingAdminUsername === user.username ? (
                          <CircularProgress size={18} />
                        ) : user.isAdmin ? (
                          <PersonOffIcon />
                        ) : (
                          <AdminPanelSettingsIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Tooltip
                  title={
                    !canModifyUser(user)
                      ? 'Cannot modify root user'
                      : user.enabled
                        ? 'Disable User'
                        : 'Enable User'
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      color={user.enabled ? 'success' : 'error'}
                      onClick={() => onToggleEnabled(user.username)}
                      aria-label={user.enabled ? 'disable user' : 'enable user'}
                      disabled={!canModifyUser(user) || togglingEnabledUsername === user.username}
                    >
                      {togglingEnabledUsername === user.username ? (
                        <CircularProgress size={18} />
                      ) : user.enabled ? (
                        <CheckCircleIcon />
                      ) : (
                        <BlockIcon />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={!canModifyUser(user) ? 'Cannot modify root user' : 'Set Password'}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => onSetPassword(user.username)}
                      aria-label="set password"
                      disabled={!canModifyUser(user)}
                    >
                      <LockResetIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={!canModifyUser(user) ? 'Cannot modify root user' : 'Delete User'}>
                  <span>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onDelete(user.username)}
                      aria-label="delete user"
                      disabled={!canModifyUser(user)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
          {users.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} align="center">
                No users found
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
